import { ApiRoutes, AppPaginationOptions, QueryError, WithPagination } from '@/common/interfaces';
import { apiExecutor, buildMutation, buildQuery } from '@/common/services';

import {
    SearchConsoleAccount,
    SearchConsoleAccountFormFields,
    SearchConsoleAccountsFormFirstStepFields, SearchConsoleAccountsFormSecondStepFields, SearchConsoleAccountsQueryKeys
} from '@/modules/DomainsApi';

export const useGetAllSearchConsoleAccountsQuery = (paginationOptions: AppPaginationOptions, search?: string) =>
  buildQuery<WithPagination<
    Pick<
      SearchConsoleAccount,
      | 'id'
      | 'email'
      | 'isReadyToUse'
      | 'refreshTokenError'
    > & { isOtpSetted: boolean }
  >, QueryError>([SearchConsoleAccountsQueryKeys.GetAll, { ...paginationOptions, search }], async () => {
    const { data } = await apiExecutor.get(ApiRoutes.DomainsApiSearchConsoleAccounts, {
      params: { ...paginationOptions, search }
    });
    return data;
  });

export const useGetByIdSearchConsoleAccountsQuery = (id: string) =>
  buildQuery<Pick<
    SearchConsoleAccount,
    | 'id'
    | 'email'
    | 'isReadyToUse'
    | 'clientId'
    | 'clientSecret'
    | 'password'
    | 'createdAt'
    | 'updatedAt'
    | 'refreshTokenLastUsedAt'
    | 'refreshTokenError'
    | 'backupEmailAddress'
  > & {
      isOtpSetted: boolean;
      isRefreshTokenSetted: boolean;
      otpAuthenticator?: Pick<NonNullable<SearchConsoleAccount['otpAuthenticator']>, 'period'>
    }, QueryError>([SearchConsoleAccountsQueryKeys.GetOneById, id], async () => {
      const { data } = await apiExecutor.get(
        ApiRoutes.DomainsApiSearchConsoleAccountsById.replace(':searchConsoleAccountId', id)
      );
      return data;
    });

export const useGetOauthUrlSearchConsoleAccountsQuery = (id: string) =>
  buildQuery<{
    id: string;
    url: string;
    password: string
  }, QueryError>([SearchConsoleAccountsQueryKeys.GetOAuthUrl, id], async () => {
    const { data } = await apiExecutor.get<{ id: string; url: string; password: string }>(
      ApiRoutes.DomainsApiSearchConsoleAccountsOAuth2Url.replace(':searchConsoleAccountId', id)
    );
    return data;
  });

export const useCreateSearchConsoleAccountsMutation = () =>
  buildMutation<SearchConsoleAccount, QueryError, SearchConsoleAccountsFormFirstStepFields>(
    [SearchConsoleAccountsQueryKeys.Create],
    async (postData) => {
      const { data } = await apiExecutor.post<SearchConsoleAccount>(ApiRoutes.DomainsApiSearchConsoleAccounts, {
        ...postData
      }, { params: { teamId: localStorage.getItem('currentTeamId') } });
      return data;
    }
  );

export const useAddCredsSearchConsoleAccountsMutation = (id: string) =>
  buildMutation<{ id: string; url: string }, QueryError, SearchConsoleAccountsFormSecondStepFields>(
    [SearchConsoleAccountsQueryKeys.AddCredentials, id],
    async (postData) => {
      const { data } = await apiExecutor.post<{ id: string; url: string }>(
        ApiRoutes.DomainsApiSearchConsoleAccountsByIdCreds.replace(':searchConsoleAccountId', id),
        {
          ...postData
        }
      );
      return data;
    }
  );

export const useDeleteSearchConsoleAccountsMutation = (searchConsoleAccountId: string) =>
  buildMutation<void, QueryError, void>([SearchConsoleAccountsQueryKeys.Delete], async () => {
    const { data } = await apiExecutor.delete<void>(
      ApiRoutes.DomainsApiSearchConsoleAccountsById.replace(':searchConsoleAccountId', searchConsoleAccountId)
    );
    return data;
  });

export const useAutocompleteSearchConsoleAccountsQuery = (params: { query: string; includedIds?: string[]; }) =>
  buildQuery<Pick<SearchConsoleAccount, 'id' | 'email' | 'isReadyToUse'>[], QueryError>(
    [SearchConsoleAccountsQueryKeys.Autocomplete, params],
    async () => {
      const { data } = await apiExecutor.get(
        ApiRoutes.DomainsApiSearchConsoleAccountsAutocomplete,
        { params: { q: params.query, includedIds: params?.includedIds?.length ? String(params.includedIds) : undefined, } }
      );
      return data;
    }
  );

export const useBreadcrumbSearchConsoleAccountsQuery = (id: string) =>
  buildQuery<string, QueryError>([SearchConsoleAccountsQueryKeys.Breadcrumb, id], async () => {
    const { data } = await apiExecutor.get<string>(
      ApiRoutes.DomainsApiSearchConsoleAccountsBreadcrumb.replace(':searchConsoleAccountId', id)
    );
    return data;
  });

export const useUpdateSearchConsoleAccountsMutation = (id: string) =>
  buildMutation<SearchConsoleAccount, QueryError, Partial<SearchConsoleAccountFormFields>>(
    [SearchConsoleAccountsQueryKeys.Update, id],
    async (postData): Promise<SearchConsoleAccount> => {
      const { data } = await apiExecutor.patch<SearchConsoleAccount>(ApiRoutes.DomainsApiSearchConsoleAccountsById.replace(':searchConsoleAccountId', id), {
        ...postData,
        id
      });
      return data;
    }
  );

export const useRevokeSearchConsoleAccountsTokenOAuth2Mutation = (id: string) =>
  buildMutation<void, QueryError, void>(
    [SearchConsoleAccountsQueryKeys.RevokeOAuth2Token, id],
    async () => {
      await apiExecutor.delete(ApiRoutes.DomainsApiSearchConsoleAccountsOAuth2.replace(':searchConsoleAccountId', id));
    }
  );
